import { BrandRailLink } from '@/components/BrandRailLink';
import { CommandMenu } from '@/components/CommandMenu';
import { ProjectSwitcher } from '@/components/ProjectSwitcher';
import { HeaderRight, TopbarUserMenu, UserMenu } from '@/components/UserMenu';
import { Separator } from '@/components/ui/separator';
import {
  SidebarContent as SidebarContentArea,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useRecentEventCount } from '@/hooks/use-events';
import { AppShell } from '@/layouts/AppShell';
import { type RailNavItem, useRailNav } from '@/lib/rail-nav';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

/**
 * One nav row. `SidebarMenuButton` carries the active state as `data-active`
 * (and the tooltip that names the row once the rail is collapsed to icons),
 * so the row is a real link and the styling lives in the primitive.
 */
function NavRow({
  item,
  active,
  badge,
  onNavigate,
}: {
  item: RailNavItem;
  active: boolean;
  /** Trailing count, e.g. the Events row's last-24h uploads. */
  badge?: { count: number; label: string };
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={item.hint ? `${item.label} — ${item.hint}` : item.label}
      >
        <Link
          to={item.to ?? item.key}
          target={item.newTab ? '_blank' : undefined}
          rel={item.newTab ? 'noreferrer' : undefined}
          onClick={onNavigate}
          title={item.hint ? `${item.label} — ${item.hint}` : undefined}
          // The chip is a bare number, so it is `aria-hidden` and the row's own
          // name carries what it counts — "Events" must not become "Events 7".
          aria-label={badge ? `${item.label}, ${badge.label}` : undefined}
          aria-current={active ? 'page' : undefined}
        >
          <Icon aria-hidden />
          <span>{item.label}</span>
          {badge && (
            // Inside the link, hugging the label: a count pinned to the far edge
            // of the rail reads as its own column, not as part of "Events".
            <span
              aria-hidden
              title={badge.label}
              className="ms-0.5 inline-flex h-4.5 min-w-4.5 shrink-0 items-center justify-center rounded-full bg-sidebar-primary px-1.5 font-semibold text-2xs text-sidebar-primary-foreground tabular-nums"
            >
              {badge.count > 999 ? '999+' : badge.count}
            </span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * The number beside the Events row: uploads in the last 24h, across every
 * project the reader can see, and the sentence that explains it (tooltip and
 * accessible name).
 *
 * A count rather than a notification — nobody has to clear it — and nothing at
 * all at zero, where a badge would be an empty box.
 */
function useRecentUploads(): { count: number; label: string } | undefined {
  const { t } = useTranslation();
  const { data } = useRecentEventCount();
  const count = data?.count ?? 0;

  if (count <= 0) return undefined;
  return {
    count,
    label: t(
      'nav.recent_uploads',
      '{{count}} uploaded in the last 24 hours, across your projects',
      { count },
    ),
  };
}

/**
 * The app rail.
 *
 * Structure comes from the shadcn sidebar: a brand block, two groups of rows
 * and the account footer. It collapses to a 48px icon rail, and every row keeps
 * its name in a tooltip; below `md` the same content opens as a sheet, which is
 * what the hand-rolled "is this md?" drawer used to do.
 *
 * Destinations only: no primary action lives here. "New analysis" is a button
 * in the events band (and a ⌘K command), because it belongs to the list it adds
 * to, not to the frame around every page.
 */
export function AppSidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { primaryItems, projectItems, footerItems, selected, roleLabel } = useRailNav();
  const uploads = useRecentUploads();

  return (
    <>
      <SidebarHeader className="gap-2">
        <BrandRailLink active={selected?.key === '/dashboard'} onNavigate={onNavigate} />
      </SidebarHeader>

      <SidebarContentArea>
        <SidebarGroup>
          <SidebarMenu>
            {primaryItems.map((n) => (
              <NavRow key={n.key} item={n} active={selected === n} onNavigate={onNavigate} />
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {/* No group header: the six rows below are the current project's, and
            the page band already names that project on every one of them. A row
            here repeating the name was one control too many (spec §28). */}
        <SidebarGroup>
          <SidebarMenu>
            {projectItems.map((n) => (
              <NavRow
                key={n.key}
                item={n}
                active={selected === n}
                // The count answers "how much is landing in my projects?", so
                // it rides the Events row rather than any one project's page.
                badge={n.key === '/events' ? uploads : undefined}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>
        {/* Pinned to the bottom, away from the navigation: neither of these is a
            place in the console, and both are read once rather than daily
            (spec §30). */}
        <SidebarGroup className="mt-auto">
          <SidebarMenu>
            {footerItems.map((n) => (
              <NavRow key={n.key} item={n} active={selected === n} onNavigate={onNavigate} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContentArea>

      <SidebarFooter>
        <UserMenu roleLabel={roleLabel} />
      </SidebarFooter>
    </>
  );
}

export function MainLayout() {
  const { pathname } = useLocation();
  return (
    <AppShell
      sidebar={<AppSidebarContent />}
      header={<CommandMenu />}
      // Which project the page below belongs to is page-level chrome, so it
      // rides in the page band next to that page's action (spec §24) instead of
      // topping the rail, where it looked like a second brand block.
      bandScope={bandScopeFor(pathname)}
      headerAfter={
        <>
          <HeaderRight />
          <Separator orientation="vertical" className="h-5" />
          <TopbarUserMenu />
        </>
      }
    />
  );
}

/**
 * The routes whose band carries the project selector: the pages that are
 * *scoped* to a project.
 *
 * The two lists, because the reader can re-scope them on the spot: they are the
 * only pages whose scope can be "all projects", so the picker carries that row
 * (spec §45) and names it while the listing is cross-project. The project's
 * sub-pages, because every one of them is that project's.
 *
 * Four kinds of page are deliberately off the list:
 *
 * - `/dashboard` is the console's cross-project landing page — the one page whose
 *   whole point is that it is not scoped to a project (spec §27). It carries the
 *   aggregation's window switch instead.
 * - `/projects` *is* a project picker. The selector above it read "Select a
 *   project" beside "New project" — two controls for one idea — and the page's
 *   own rows open a project anyway.
 * - a *record* (`/cases/:id`, `/devices/:id`) belongs to the project it belongs
 *   to: switching projects there cannot re-scope anything, it only abandons the
 *   page. The breadcrumb says which project it is and the header's back/forward
 *   pair is the way out (spec §26). Event records use their own standalone
 *   layout instead.
 * - the project's devices list, because it is a table like Events and Cases:
 *   its filter row owns the scope selector rather than the page band.
 * - `/account` and `/download` are not about a project at all.
 */
function bandScopeFor(pathname: string): ReactNode {
  const projectPage = /^\/projects\/[^/]+(\/|$)/.test(pathname);
  const devicesList = /^\/projects\/[^/]+\/devices\/?$/.test(pathname);
  return projectPage && !devicesList ? <ProjectSwitcher /> : undefined;
}
